import { getGlobalThat } from '@bcf-vanilla-ts-v1-shared/di/global-that';
import type { Observable } from 'rxjs';
import { Subject } from 'rxjs';

import type {
  AppTsCssVars as HC_AppTsCssVars,
  PrimitiveTsCssVars as HC_PrimitiveTsCssVars,
  SemanticTsCssVars as HC_SemanticTsCssVars
} from './../css/homer-casino/mobile/variables.ts-css-var';
import type {
  AppTsCssVars as JZD_AppTsCssVars,
  PrimitiveTsCssVars as JZD_PrimitiveTsCssVars,
  SemanticTsCssVars as JZD_SemanticTsCssVars
} from './../css/janusz-casino/mobile/variables-dark.ts-css-var';
import type {
  AppTsCssVars as JZ_AppTsCssVars,
  PrimitiveTsCssVars as JZ_PrimitiveTsCssVars,
  SemanticTsCssVars as JZ_SemanticTsCssVars
} from './../css/janusz-casino/mobile/variables.ts-css-var';
import type {
  AppTsCssVars as KS_AppTsCssVars,
  PrimitiveTsCssVars as KS_PrimitiveTsCssVars,
  SemanticTsCssVars as KS_SemanticTsCssVars
} from './../css/kingspin/mobile/variables.ts-css-var';
import type {
  AppTsCssVars as TK_AppTsCssVars,
  PrimitiveTsCssVars as TK_PrimitiveTsCssVars,
  SemanticTsCssVars as TK_SemanticTsCssVars
} from './../css/tikal-casino/mobile/variables.ts-css-var';

/**
 * Single source of truth: one slot per brand. When adding a theme, extend these tuples and import the new types above.
 */
type BrandAppTuple = readonly [JZ_AppTsCssVars, TK_AppTsCssVars, HC_AppTsCssVars, KS_AppTsCssVars, JZD_AppTsCssVars];

type BrandPrimitiveTuple = readonly [
  JZ_PrimitiveTsCssVars,
  TK_PrimitiveTsCssVars,
  HC_PrimitiveTsCssVars,
  KS_PrimitiveTsCssVars,
  JZD_PrimitiveTsCssVars
];

type BrandSemanticTuple = readonly [
  JZ_SemanticTsCssVars,
  TK_SemanticTsCssVars,
  HC_SemanticTsCssVars,
  KS_SemanticTsCssVars,
  JZD_SemanticTsCssVars
];

/** Full token map for any supported brand (runtime). */
export type AllAppTsCssVars = BrandAppTuple[number];

/** Primitive slice only — per-brand palettes may differ (see `PrimitiveTsCssVarKey`). */
export type AllPrimitiveTsCssVars = BrandPrimitiveTuple[number];

/** Semantic slice only — aligned across brands in Figma (see `SemanticTsCssVarKey`). */
export type AllSemanticTsCssVars = BrandSemanticTuple[number];

type IntersectKeys<T extends readonly object[]> = T extends readonly [
  infer Head extends object,
  ...infer Rest extends readonly object[]
]
  ? Rest extends readonly []
    ? keyof Head
    : keyof Head & IntersectKeys<Rest>
  : never;

/** `keyof A & keyof B & …` — keys present on every brand’s semantic map (matches verify script). */
export type SemanticTsCssVarKey = IntersectKeys<BrandSemanticTuple>;

/** `keyof` distributed over the union of brand primitives — key exists on at least one palette. */
type KeysOfUnion<U> = U extends U ? keyof U : never;

export type PrimitiveTsCssVarKey = KeysOfUnion<BrandPrimitiveTuple[number]>;

/** Any key from any brand’s full map (`getTsCssVar`). */
export type AppTsCssVarKey = KeysOfUnion<BrandAppTuple[number]>;

type ValueForKeyInTuple<K extends PropertyKey, T extends readonly object[]> = T extends readonly [
  infer Head extends object,
  ...infer Rest extends readonly object[]
]
  ? (K extends keyof Head ? Head[K] : never) | ValueForKeyInTuple<K, Rest>
  : never;

/** Value type for `key` across all brand maps (union of each brand’s entry when present). */
export type AppTsCssVarValue<K extends AppTsCssVarKey> = ValueForKeyInTuple<K, BrandAppTuple>;

/**
 * The `Subject` itself is stored on the global store (like `_TS_CSS_VARS`) rather than as a
 * module-level singleton, so every bundle that imports this file talks to the same instance
 * instead of each getting its own disconnected `Subject`.
 */
function _getTsCssVarChangedSubject(): Subject<void> {
  const globalAny: any = getGlobalThat();
  if (!globalAny['_TS_CSS_VARS_CHANGED$']) {
    globalAny['_TS_CSS_VARS_CHANGED$'] = new Subject<void>();
  }
  return globalAny['_TS_CSS_VARS_CHANGED$'] as Subject<void>;
}

export function setTsCssVar(variables: AllAppTsCssVars): void {
  const globalAny: any = getGlobalThat();
  if (!globalAny['_TS_CSS_VARS']) {
    globalAny['_TS_CSS_VARS'] = {};
  }
  globalAny['_TS_CSS_VARS'] = variables;
  _getTsCssVarChangedSubject().next();
}

export function getTsCssVar<K extends AppTsCssVarKey>(key: K, fallbackKey?: K): AppTsCssVarValue<K> {
  const globalAny: any = getGlobalThat();
  if (!globalAny['_TS_CSS_VARS']) {
    globalAny['_TS_CSS_VARS'] = {};
  }
  if (fallbackKey) {
    return (globalAny['_TS_CSS_VARS'][key] ?? globalAny['_TS_CSS_VARS'][fallbackKey]) as AppTsCssVarValue<K>;
  }
  return globalAny['_TS_CSS_VARS'][key] as AppTsCssVarValue<K>;
}

// Decision: see ADR-0018
/**
 * Emits whenever `setTsCssVar` stores a new theme map (runtime theme swap). Building a plain
 * `Observable` (no DOM/browser API involved) is SSR-safe by construction — consumers that react
 * to it with browser-only work (e.g. re-reading computed styles) must still guard themselves with
 * `__RENDER_MODE__ === 'browser'`, same as everywhere else in this codebase.
 */
export function getTsCssVarChanged$(): Observable<void> {
  return _getTsCssVarChangedSubject().asObservable();
}
